import axios from 'axios';
import * as constants from '../constants/CONSTANT';

class AttachmentApi {
  constructor() {
    this.baseUrl = `${constants.API_BASE_URL}/api/attachments`;
    this.token = localStorage.getItem('token');
  }

  /**
   * Upload attachments for a workflow action
   * @param {string} workflowId - Workflow ID
   * @param {number} actionIndex - Action index (0-based)
   * @param {File[]} files - Array of files to upload
   * @param {string} description - Optional description
   * @returns {Promise<{success: boolean, data: Array, message: string}>}
   */
  async uploadWorkflowAttachments(workflowId, actionIndex, files, description = '') {
    try {
      if (!files || files.length === 0) {
        throw new Error('No files provided');
      }

      const formData = new FormData();

      // Append files
      if (Array.isArray(files)) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      } else {
        formData.append('files', files);
      }

      // Append description if provided
      if (description) {
        formData.append('description', description);
      }

      const response = await axios.post(
        `${this.baseUrl}/workflow/${workflowId}/${actionIndex}`,
        formData,
        {
          headers: {
            Authorization: this.token,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error uploading workflow attachments:', error);
      throw error;
    }
  }

  /**
   * Get attachments for a workflow action
   * @param {string} workflowId - Workflow ID
   * @param {number} actionIndex - Action index (0-based)
   * @returns {Promise<{success: boolean, data: Array, count: number}>}
   */
  async getWorkflowAttachments(workflowId, actionIndex) {
    try {
      const response = await axios.get(
        `${this.baseUrl}/workflow/${workflowId}/${actionIndex}`,
        {
          headers: {
            Authorization: this.token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching workflow attachments:', error);
      throw error;
    }
  }

  /**
   * Get all attachments for a workflow
   * @param {string} workflowId - Workflow ID
   * @returns {Promise<{success: boolean, data: Array, count: number}>}
   */
  async getAllWorkflowAttachments(workflowId) {
    try {
      const response = await axios.get(
        `${this.baseUrl}/workflow/${workflowId}`,
        {
          headers: {
            Authorization: this.token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching workflow attachments:', error);
      throw error;
    }
  }

  /**
   * Delete an attachment
   * @param {string} attachmentId - Attachment ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteAttachment(attachmentId) {
    try {
      const cleanId = (attachmentId && typeof attachmentId === 'object')
        ? (attachmentId.value || attachmentId.id || attachmentId.label || '')
        : attachmentId;
      const response = await axios.delete(
        `${this.baseUrl}/${cleanId}`,
        {
          headers: {
            Authorization: this.token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  }

  /**
   * Download an attachment
   * @param {string} attachmentId - Attachment ID
   * @returns {Promise<Blob>}
   */
  async downloadAttachment(attachmentId) {
    try {
      const cleanId = (attachmentId && typeof attachmentId === 'object')
        ? (attachmentId.value || attachmentId.id || attachmentId.label || '')
        : attachmentId;
      const response = await axios.get(
        `${this.baseUrl}/download/${cleanId}`,
        {
          headers: {
            Authorization: this.token,
          },
          responseType: 'blob',
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  }
}

export default new AttachmentApi();
